/**
 * QBridgeHelpModal Component (E11)
 * Comprehensive help modal with installation guide and troubleshooting
 *
 * Features:
 * - Step-by-step installation guide
 * - Troubleshooting FAQ accordion
 * - Link to AI help in chat
 * - Download button integration
 * - Matches platform styling
 */

import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  Button,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Tabs,
  Tab
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DownloadIcon from '@mui/icons-material/Download';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ComputerIcon from '@mui/icons-material/Computer';
import EmailIcon from '@mui/icons-material/Email';
import SecurityIcon from '@mui/icons-material/Security';
import ChatIcon from '@mui/icons-material/Chat';
import LinkIcon from '@mui/icons-material/Link';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import QBridgeDownloadButton from './QBridgeDownloadButton';

// ═══════════════════════════════════════════════════════════════════════════════
// CONSTANTS
// ═══════════════════════════════════════════════════════════════════════════════

const COLORS = {
  background: '#0F0F14',
  surface: 'rgba(255, 255, 255, 0.02)',
  surfaceHover: 'rgba(255, 255, 255, 0.04)',
  border: 'rgba(255, 255, 255, 0.1)',
  borderLight: 'rgba(255, 255, 255, 0.08)',
  text: '#FFFFFF',
  textSecondary: 'rgba(255, 255, 255, 0.7)',
  textMuted: 'rgba(255, 255, 255, 0.5)',
  primary: '#27B56D',
  primaryLight: 'rgba(39, 181, 109, 0.1)',
  primaryBorder: 'rgba(39, 181, 109, 0.3)',
  accent: '#7c3aed',
  accentLight: 'rgba(124, 58, 237, 0.1)',
  error: '#EF4444',
  warning: '#F59E0B'
};

const INSTALLATION_STEPS = [
  {
    icon: <DownloadIcon />,
    title: 'Download QBridge + Q.AI Panel',
    description: 'Click the download button to get the installer (15 MB)',
    detail: 'One installer includes QBridge service and Q.AI Panel EA for MT5 — everything you need.'
  },
  {
    icon: <PlayArrowIcon />,
    title: 'Run the Installer',
    description: 'Double-click and follow prompts (Next → Accept → Install → Finish)',
    detail: 'Takes ~30 seconds. QBridge auto-starts and deploys Q.AI Panel to all MT5 terminals.'
  },
  {
    icon: <ComputerIcon />,
    title: 'Open MT5',
    description: 'Launch MetaTrader 5 and open the Navigator panel (Ctrl+N)',
    detail: 'Q.AI Panel v3 is already installed in your Expert Advisors folder automatically.'
  },
  {
    icon: <LinkIcon />,
    title: 'Drag Panel to Chart',
    description: 'Drag QAI_Panel_v3 onto any chart and click "GET Q.AI ANALYSIS"',
    detail: 'That\'s it! Start getting AI-powered trade analysis directly in MT5.'
  }
];

const TROUBLESHOOTING_FAQ = [
  {
    question: '"MT5 not detected" — what should I do?',
    answer: 'Ensure MetaTrader 5 is installed on your computer (not just MT4). QBridge auto-scans common install paths. If MT5 is in a custom folder, the wizard lets you manually browse to select it.'
  },
  {
    question: '"Import failed" or "Connection error"',
    answer: 'Check your internet connection and try again. If the issue persists: (1) Make sure MT5 is open and logged in, (2) Verify firewall isn\'t blocking QBridge, (3) Restart the wizard.'
  },
  {
    question: 'No trades imported — why?',
    answer: 'QBridge imports closed trades only. If you\'re new to trading or haven\'t closed any positions yet, there\'s nothing to import. Open trades sync automatically once closed.'
  },
  {
    question: 'Windows "SmartScreen" warning',
    answer: 'This is normal for new apps not from Microsoft Store. Click "More info" → "Run anyway". QBridge is safe — it only reads trade history (read-only) and never accesses passwords.'
  },
  {
    question: 'QBridge installer won\'t run',
    answer: 'Try: (1) Right-click → "Run as administrator", (2) Temporarily disable antivirus, (3) Ensure you have Windows 10/11. Still stuck? Ask Q for help — it\'s FREE!'
  },
  {
    question: 'How do I re-sync or update data?',
    answer: 'QBridge syncs automatically in the background. To force a re-sync, open QBridge from your system tray and click "Sync Now". Updates install automatically.'
  }
];

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * QBridgeHelpModal
 * @param {boolean} open - Modal visibility
 * @param {function} onClose - Close handler
 * @param {function} onAskHelp - Handler to trigger AI help in chat
 * @param {number} initialTab - Starting tab (0 = Guide, 1 = Troubleshoot)
 */
const QBridgeHelpModal = ({
  open = false,
  onClose,
  onAskHelp,
  initialTab = 0
}) => {
  const [activeTab, setActiveTab] = useState(initialTab);
  const [expandedFaq, setExpandedFaq] = useState(null);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleFaqChange = (panel) => (event, isExpanded) => {
    setExpandedFaq(isExpanded ? panel : null);
  };

  const handleAskHelp = () => {
    onAskHelp?.();
    onClose?.();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: COLORS.background,
          backgroundImage: 'none',
          border: `1px solid ${COLORS.border}`,
          borderRadius: '16px',
          maxWidth: '520px',
          maxHeight: '85vh'
        }
      }}
    >
      {/* ════════════════════════════════════════════════════════════════════════
          HEADER
          ════════════════════════════════════════════════════════════════════════ */}
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '20px 24px 12px',
          borderBottom: `1px solid ${COLORS.borderLight}`
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '10px',
              backgroundColor: COLORS.accentLight,
              border: `1px solid rgba(124, 58, 237, 0.2)`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <HelpOutlineIcon sx={{ color: COLORS.accent, fontSize: '1.25rem' }} />
          </Box>
          <Box>
            <Typography
              sx={{
                color: COLORS.text,
                fontSize: '1.25rem',
                fontWeight: 700,
                fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
                letterSpacing: '-0.01em'
              }}
            >
              QBridge Setup Guide
            </Typography>
            <Typography
              sx={{
                color: COLORS.textMuted,
                fontSize: '0.875rem',
                fontWeight: 500
              }}
            >
              Connect MT5 in under 2 minutes
            </Typography>
          </Box>
        </Box>

        <IconButton
          onClick={onClose}
          sx={{
            color: COLORS.textMuted,
            '&:hover': {
              color: COLORS.text,
              backgroundColor: COLORS.surfaceHover
            }
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {/* ════════════════════════════════════════════════════════════════════════
          TABS
          ════════════════════════════════════════════════════════════════════════ */}
      <Box sx={{ borderBottom: `1px solid ${COLORS.borderLight}` }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="fullWidth"
          sx={{
            minHeight: 44,
            '& .MuiTab-root': {
              color: COLORS.textMuted,
              textTransform: 'none',
              fontSize: '0.875rem',
              fontWeight: 500,
              minHeight: 44,
              '&.Mui-selected': {
                color: COLORS.primary
              }
            },
            '& .MuiTabs-indicator': {
              backgroundColor: COLORS.primary,
              height: 2
            }
          }}
        >
          <Tab
            icon={<LinkIcon sx={{ fontSize: '1.125rem' }} />}
            iconPosition="start"
            label="Setup Guide"
          />
          <Tab
            icon={<ErrorOutlineIcon sx={{ fontSize: '1.125rem' }} />}
            iconPosition="start"
            label="Troubleshoot"
          />
        </Tabs>
      </Box>

      {/* ════════════════════════════════════════════════════════════════════════
          CONTENT
          ════════════════════════════════════════════════════════════════════════ */}
      <DialogContent sx={{ padding: 0 }}>
        {/* Tab 0: Setup Guide */}
        {activeTab === 0 && (
          <Box sx={{ padding: '20px 24px' }}>
            {/* Download Button */}
            <Box sx={{ mb: 3 }}>
              <QBridgeDownloadButton variant="secondary" showDetails={true} />
            </Box>

            <Divider sx={{ borderColor: COLORS.borderLight, mb: 3 }} />

            {/* Installation Steps */}
            <Typography
              sx={{
                color: COLORS.text,
                fontSize: '1rem',
                fontWeight: 700,
                mb: 2,
                letterSpacing: '-0.01em'
              }}
            >
              Quick Setup (4 Steps)
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              {INSTALLATION_STEPS.map((step, index) => (
                <Box key={index} sx={{ display: 'flex', gap: 1.5 }}>
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: '8px',
                      backgroundColor: COLORS.primaryLight,
                      border: `1px solid ${COLORS.primaryBorder}`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}
                  >
                    <Typography
                      sx={{
                        color: COLORS.primary,
                        fontSize: '0.875rem',
                        fontWeight: 700
                      }}
                    >
                      {index + 1}
                    </Typography>
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      sx={{
                        color: COLORS.text,
                        fontSize: '0.9375rem',
                        fontWeight: 600,
                        mb: 0.25,
                        letterSpacing: '-0.01em'
                      }}
                    >
                      {step.title}
                    </Typography>
                    <Typography
                      sx={{
                        color: COLORS.textSecondary,
                        fontSize: '0.875rem',
                        mb: 0.5,
                        lineHeight: 1.5
                      }}
                    >
                      {step.description}
                    </Typography>
                    <Typography
                      sx={{
                        color: COLORS.textMuted,
                        fontSize: '0.8125rem',
                        lineHeight: 1.5
                      }}
                    >
                      {step.detail}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>

            {/* Security Info */}
            <Box
              sx={{
                mt: 3,
                padding: '12px 16px',
                backgroundColor: COLORS.primaryLight,
                border: `1px solid ${COLORS.primaryBorder}`,
                borderRadius: '10px'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <SecurityIcon sx={{ color: COLORS.primary, fontSize: '1rem' }} />
                <Typography
                  sx={{
                    color: COLORS.text,
                    fontSize: '0.8125rem',
                    fontWeight: 600
                  }}
                >
                  Your data is safe
                </Typography>
              </Box>
              <Typography sx={{ color: COLORS.textSecondary, fontSize: '0.75rem' }}>
                QBridge runs locally on your PC. It only reads trade history (read-only) and never
                accesses your MT5 password. Data is encrypted in transit.
              </Typography>
            </Box>

            {/* User Guide Link */}
            <Box
              sx={{
                mt: 2,
                display: 'flex',
                justifyContent: 'center'
              }}
            >
              <Button
                component="a"
                href="/docs/user-guide"
                target="_blank"
                rel="noopener noreferrer"
                startIcon={<MenuBookIcon sx={{ fontSize: '1rem' }} />}
                sx={{
                  color: COLORS.textSecondary,
                  textTransform: 'none',
                  fontSize: '0.8125rem',
                  '&:hover': {
                    color: COLORS.accent,
                    backgroundColor: 'transparent'
                  }
                }}
              >
                Read Full User Guide
              </Button>
            </Box>
          </Box>
        )}

        {/* Tab 1: Troubleshooting */}
        {activeTab === 1 && (
          <Box sx={{ padding: '20px 24px' }}>
            <Typography
              sx={{
                color: COLORS.text,
                fontSize: '0.9375rem',
                fontWeight: 600,
                mb: 2
              }}
            >
              Common Issues
            </Typography>

            <Box sx={{ mb: 3 }}>
              {TROUBLESHOOTING_FAQ.map((faq, index) => (
                <Accordion
                  key={index}
                  expanded={expandedFaq === index}
                  onChange={handleFaqChange(index)}
                  disableGutters
                  elevation={0}
                  sx={{
                    backgroundColor: 'transparent',
                    border: `1px solid ${COLORS.borderLight}`,
                    borderRadius: '8px !important',
                    mb: 1,
                    '&:before': { display: 'none' },
                    '&.Mui-expanded': {
                      borderColor: COLORS.primary,
                      backgroundColor: COLORS.surface
                    }
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon sx={{ color: COLORS.textMuted }} />}
                    sx={{
                      minHeight: 48,
                      '& .MuiAccordionSummary-content': {
                        margin: '12px 0'
                      }
                    }}
                  >
                    <Typography
                      sx={{
                        color: expandedFaq === index ? COLORS.text : COLORS.textSecondary,
                        fontSize: '0.875rem',
                        fontWeight: 500
                      }}
                    >
                      {faq.question}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ pt: 0, pb: 2 }}>
                    <Typography
                      sx={{
                        color: COLORS.textMuted,
                        fontSize: '0.8125rem',
                        lineHeight: 1.6
                      }}
                    >
                      {faq.answer}
                    </Typography>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>

            {/* AI Help CTA */}
            <Box
              sx={{
                padding: '16px',
                backgroundColor: COLORS.accentLight,
                border: `1px solid rgba(124, 58, 237, 0.2)`,
                borderRadius: '12px',
                textAlign: 'center'
              }}
            >
              <ChatIcon sx={{ color: COLORS.accent, fontSize: '1.5rem', mb: 1 }} />
              <Typography
                sx={{
                  color: COLORS.text,
                  fontSize: '0.9375rem',
                  fontWeight: 600,
                  mb: 0.5
                }}
              >
                Still need help?
              </Typography>
              <Typography
                sx={{
                  color: COLORS.textMuted,
                  fontSize: '0.8125rem',
                  mb: 2
                }}
              >
                Ask Q for personalized assistance — it's FREE for installation help!
              </Typography>
              <Button
                onClick={handleAskHelp}
                fullWidth
                startIcon={<ChatIcon />}
                sx={{
                  background: `linear-gradient(135deg, ${COLORS.accent} 0%, #a855f7 100%)`,
                  color: '#FFFFFF',
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  padding: '10px 20px',
                  borderRadius: '8px',
                  '&:hover': {
                    background: `linear-gradient(135deg, #6d28d9 0%, #9333ea 100%)`
                  }
                }}
              >
                Ask Q for Help
              </Button>
              <Typography
                sx={{
                  color: COLORS.textMuted,
                  fontSize: '0.75rem',
                  mt: 1,
                  fontStyle: 'italic'
                }}
              >
                Type "Help me install QBridge" in the chat
              </Typography>
            </Box>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default QBridgeHelpModal;
